const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

const syncVideoControl = (button, video) => {
  const paused = video.paused;
  button.setAttribute('aria-pressed', String(paused));
  button.setAttribute('aria-label', paused ? 'Play campaign film' : 'Pause campaign film');
  button.querySelector('[data-video-icon="pause"]')?.toggleAttribute('hidden', paused);
  button.querySelector('[data-video-icon="play"]')?.toggleAttribute('hidden', !paused);
};

class NuvryaHero {
  constructor(section) {
    if (section.dataset.heroReady === 'true') return;
    section.dataset.heroReady = 'true';

    this.section = section;
    this.slides = [...section.querySelectorAll('[data-nuvrya-hero-slide]')];
    this.dots = [...section.querySelectorAll('[data-nuvrya-hero-dot]')];
    this.count = section.querySelector('[data-nuvrya-hero-count]');
    this.toggle = section.querySelector('[data-nuvrya-hero-toggle]');
    this.index = 0;
    // Measured on the reference: 5000ms autoplay. Mirrored by the progress
    // animation in nuvrya-campaign.css — change both together.
    this.delay = 5000;
    this.visible = true;
    this.isPlaying = !reducedMotion.matches;
    this.timer = 0;

    section.querySelector('[data-nuvrya-hero-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    section.querySelector('[data-nuvrya-hero-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot) => dot.addEventListener('click', () => this.go(Number(dot.dataset.nuvryaHeroDot))));
    this.toggle?.addEventListener('click', () => this.setPlaying(!this.isPlaying));

    if (finePointer.matches) {
      section.addEventListener('pointermove', (event) => {
        const bounds = section.getBoundingClientRect();
        section.style.setProperty('--hero-pointer-x', ((event.clientX - bounds.left) / bounds.width - 0.5).toFixed(3));
        section.style.setProperty('--hero-pointer-y', ((event.clientY - bounds.top) / bounds.height - 0.5).toFixed(3));
      });
      section.addEventListener('pointerleave', () => {
        section.style.setProperty('--hero-pointer-x', '0');
        section.style.setProperty('--hero-pointer-y', '0');
      });
    }

    new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) {
        this.playActiveVideo();
        this.schedule();
      } else {
        window.clearTimeout(this.timer);
        this.slides.forEach((slide) => slide.querySelector('video')?.pause());
      }
    }, { threshold: 0.15 }).observe(section);

    this.updateToggle();
    this.go(0, false);
  }

  go(requestedIndex, restart = true) {
    if (!this.slides.length) return;
    this.index = (requestedIndex + this.slides.length) % this.slides.length;

    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', String(!active));
      const video = slide.querySelector('video');
      if (video && !active) video.pause();
    });

    this.dots.forEach((dot, index) => {
      const active = index === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', String(active));
      dot.tabIndex = active ? 0 : -1;
    });

    if (this.count) this.count.textContent = String(this.index + 1).padStart(2, '0');
    this.playActiveVideo();
    if (restart) this.restartProgress();
    this.schedule();
  }

  playActiveVideo() {
    const video = this.slides[this.index]?.querySelector('video');
    if (!video || !this.isPlaying || !this.visible || reducedMotion.matches) return;
    video.play().catch(() => {});
  }

  schedule() {
    window.clearTimeout(this.timer);
    if (!this.isPlaying || !this.visible || reducedMotion.matches) return;
    this.timer = window.setTimeout(() => this.go(this.index + 1), this.delay);
  }

  restartProgress() {
    this.section.classList.remove('is-rotating');
    void this.section.offsetWidth;
    if (this.isPlaying) this.section.classList.add('is-rotating');
  }

  setPlaying(playing) {
    this.isPlaying = playing && !reducedMotion.matches;
    if (!this.isPlaying) {
      window.clearTimeout(this.timer);
      this.slides.forEach((slide) => slide.querySelector('video')?.pause());
    } else {
      this.playActiveVideo();
    }
    this.updateToggle();
    this.restartProgress();
    this.schedule();
  }

  updateToggle() {
    if (!this.toggle) return;
    this.toggle.setAttribute('aria-pressed', String(!this.isPlaying));
    this.toggle.setAttribute('aria-label', this.isPlaying ? 'Pause campaign rotation' : 'Play campaign rotation');
    this.toggle.querySelector('[data-video-icon="pause"]')?.toggleAttribute('hidden', !this.isPlaying);
    this.toggle.querySelector('[data-video-icon="play"]')?.toggleAttribute('hidden', this.isPlaying);
    this.section.classList.toggle('is-rotating', this.isPlaying);
  }
}

const initStandaloneVideos = (scope = document) => {
  scope.querySelectorAll('[data-nuvrya-video-section]').forEach((section) => {
    if (section.dataset.videoReady === 'true') return;
    section.dataset.videoReady = 'true';
    const video = section.querySelector('video');
    const button = section.querySelector('[data-nuvrya-video-control]');
    if (!video || !button) return;

    if (reducedMotion.matches) video.pause();
    button.addEventListener('click', async () => {
      if (video.paused) await video.play().catch(() => {});
      else video.pause();
      syncVideoControl(button, video);
    });
    video.addEventListener('play', () => syncVideoControl(button, video));
    video.addEventListener('pause', () => syncVideoControl(button, video));
    syncVideoControl(button, video);
  });
};

const initReels = (scope = document) => {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const video = entry.target;
      if (entry.isIntersecting && !reducedMotion.matches) video.play().catch(() => {});
      else video.pause();
    });
  }, { threshold: 0.35 });

  scope.querySelectorAll('[data-nuvrya-reel-video]').forEach((video) => {
    if (video.dataset.reelReady === 'true') return;
    video.dataset.reelReady = 'true';
    observer.observe(video);
  });
};

const initReveal = (scope = document) => {
  if (reducedMotion.matches) return;
  document.documentElement.classList.add('nuvrya-motion-enabled');
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-in-view');
      observer.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  scope.querySelectorAll('[data-nuvrya-reveal]').forEach((element, index) => {
    if (element.dataset.revealReady === 'true') return;
    element.dataset.revealReady = 'true';
    element.style.transitionDelay = `${Math.min(index % 4, 3) * 70}ms`;
    observer.observe(element);
  });
};

const initCampaign = (scope = document) => {
  scope.querySelectorAll('[data-nuvrya-hero]').forEach((section) => new NuvryaHero(section));
  initStandaloneVideos(scope);
  initReels(scope);
  initReveal(scope);
};

initCampaign();
document.addEventListener('shopify:section:load', (event) => initCampaign(event.target));
